'use client';

import { useAuth } from '@clerk/nextjs';
import type { Row } from '@rocicorp/zero';
import { useQuery } from '@rocicorp/zero/react';
import type { OrganizationUserRole } from '@ent/permissions-shared';
import { primaryRole } from '@utils/capabilities';
import { schema } from '../zero-schema';
import { queries } from './queries';

export type CurrentUser = Row<(typeof schema)['tables']['users']>;
export type CurrentOrgUser = Row<
  (typeof schema)['tables']['organizationUsers']
>;

/**
 * Zero `users` row for the signed-in Clerk user, or `undefined` while the
 * row is loading / when there is no Clerk id yet. Must run under
 * `ZeroClientProvider`.
 */
export function useCurrentUser(): CurrentUser | undefined {
  const { userId } = useAuth();
  const [currentUser] = useQuery(userId ? queries.users.current() : undefined);
  return currentUser;
}

/**
 * Zero `organizationUsers` row for the signed-in user in `organizationId`,
 * or `undefined` until that membership loads. A user can hold one row per
 * role; the most privileged one is returned.
 */
export function useCurrentOrgUser(
  organizationId: string
): CurrentOrgUser | undefined {
  const { userId } = useAuth();
  const [orgUsers] = useQuery(
    userId
      ? queries.organizationUsers.currentMembership({ organizationId })
      : undefined
  );
  if (!orgUsers?.length) return undefined;
  const role = primaryRole(
    orgUsers.map(row => row.role as OrganizationUserRole | null)
  );
  return orgUsers.find(row => row.role === role) ?? orgUsers[0];
}
